import axios from 'axios';
import { safeEntityRef, sanitizeAnalyticsPath, trackApiError, trackEvent } from './track';

// Same-origin by default so one build serves every domain the platform runs
// on (content.thedylanewing.com, content.geniusxai.com, ...). The env var
// stays as a dev-time override (local :3000 talking to a remote backend).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('glowup_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses. Auth endpoints are exempt: a wrong password on
// /auth/login is a 401 too, and redirecting there reloads /login before the
// error toast can render, so the user gets a silent hard reload instead of
// "Invalid email or password".
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const reqUrl = error.config?.url || '';
    const isAuthCall = reqUrl.includes('/auth/');
    try {
      trackApiError(error);
    } catch {
      // Analytics must never change the request's normal error handling.
    }
    if (error.response?.status === 401 && !isAuthCall) {
      localStorage.removeItem('glowup_token');
      localStorage.removeItem('glowup_user');
      window.location.href = '/login';
    }
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      window.dispatchEvent(new CustomEvent('glowup:upgrade-required', {
        detail: {
          ...(typeof detail === 'object' && detail ? detail : { message: String(detail || 'Upgrade required') }),
          request_url: reqUrl,
        },
      }));
    }
    return Promise.reject(error);
  }
);

// Auth helpers
export const authApi = {
  register: async (email, password, name, extras = {}) => {
    // Never inherit a prior user's cached workspace on a shared browser.
    localStorage.removeItem('glowup_workspace');
    // extras: { attribution, plan_intent } for ads conversion reporting.
    const { data } = await api.post('/auth/register', { email, password, name, ...extras });
    localStorage.setItem('glowup_token', data.token);
    localStorage.setItem('glowup_user', JSON.stringify(data.user));
    // Adopt the auto-provisioned starter workspace so the user lands in setup.
    if (data.workspace) {
      localStorage.setItem('glowup_workspace', JSON.stringify(data.workspace));
    }
    return data;
  },
  login: async (email, password) => {
    // Clear any stale workspace from a previous session/user before signing in.
    localStorage.removeItem('glowup_workspace');
    const { data } = await api.post('/auth/login', { email, password });
    if (data.redirect_url) return data;
    localStorage.setItem('glowup_token', data.token);
    localStorage.setItem('glowup_user', JSON.stringify(data.user));
    return data;
  },
  logout: () => {
    localStorage.removeItem('glowup_token');
    localStorage.removeItem('glowup_user');
    localStorage.removeItem('glowup_workspace');
  },
  getMe: async () => {
    const { data } = await api.get('/auth/me');
    return data;
  },
  adMeasurement: async () => (await api.get('/auth/ad-measurement')).data,
  revokeAdMeasurement: async () => (await api.delete('/auth/ad-measurement')).data,
  isAuthenticated: () => !!localStorage.getItem('glowup_token'),
  getCurrentUser: () => {
    const user = localStorage.getItem('glowup_user');
    return user ? JSON.parse(user) : null;
  },
  changePassword: async (currentPassword, newPassword) => {
    const { data } = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // Changing the password revokes every session's token (token_version
    // bump); adopt the fresh one so THIS session stays logged in.
    if (data?.token) localStorage.setItem('glowup_token', data.token);
    return data;
  },
  inspectPasswordSetup: async (token) => {
    const { data } = await api.post('/auth/password-setup/inspect', { token });
    return data;
  },
  completePasswordSetup: async (token, newPassword) => {
    // A provisioned setup replaces any stale session on a shared browser.
    localStorage.removeItem('glowup_workspace');
    const { data } = await api.post('/auth/password-setup/complete', {
      token,
      new_password: newPassword,
    });
    localStorage.setItem('glowup_token', data.token);
    localStorage.setItem('glowup_user', JSON.stringify(data.user));
    if (data.workspace) localStorage.setItem('glowup_workspace', JSON.stringify(data.workspace));
    return data;
  },
};

// Workspace helpers
export const workspaceApi = {
  list: async () => {
    const { data } = await api.get('/workspaces');
    return data;
  },
  create: async (name, type, niche) => {
    const { data } = await api.post('/workspaces', { name, type, niche });
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/workspaces/${id}`);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await api.put(`/workspaces/${id}`, updates);
    return data;
  },
  getPostVisuals: async (id) => {
    const { data } = await api.get(`/workspaces/${id}/photos`);
    return data;
  },
  setImageStyle: async (id, imageStyle) => {
    const { data } = await api.put(`/workspaces/${id}/image-style`, { image_style: imageStyle });
    return data;
  },
  getContentTypes: async (id) => {
    const { data } = await api.get(`/workspaces/${id}/content-types`);
    return data;
  },
  setContentTypes: async (id, contentTypes) => {
    const { data } = await api.put(`/workspaces/${id}/content-types`, { content_types: contentTypes });
    return data;
  },
  remove: async (id) => {
    const { data } = await api.delete(`/workspaces/${id}`);
    return data;
  },
  getCurrent: () => {
    const ws = localStorage.getItem('glowup_workspace');
    return ws ? JSON.parse(ws) : null;
  },
  setCurrent: (workspace) => {
    localStorage.setItem('glowup_workspace', JSON.stringify(workspace));
  },
  clearCurrent: () => {
    localStorage.removeItem('glowup_workspace');
  },
};

const currentProductEventProps = () => {
  const workspace = workspaceApi.getCurrent();
  return {
    workspace_ref: safeEntityRef(workspace?.id),
    workspace_type: String(workspace?.type || 'unknown').slice(0, 40),
    surface: sanitizeAnalyticsPath(window.location.pathname),
  };
};

const deliveryState = (delivery) => {
  if (delivery?.already_queued) return 'already_queued';
  if (delivery?.scheduled) return 'scheduled';
  if (delivery?.reason) return 'approved_not_scheduled';
  return 'approved';
};

const lengthBucket = (value) => {
  const length = String(value || '').trim().length;
  if (length === 0) return 'empty';
  if (length <= 40) return 'short';
  if (length <= 280) return 'medium';
  return 'long';
};

// Reel helpers
export const reelApi = {
  list: async (workspaceId) => {
    const { data } = await api.get(`/reels?workspace_id=${workspaceId}`);
    return data;
  },
  create: async (workspaceId, topic, niche, audience) => {
    const { data } = await api.post('/reels', {
      workspace_id: workspaceId,
      topic,
      niche,
      target_audience: audience,
    });
    return data;
  },
  get: async (id) => {
    const { data } = await api.get(`/reels/${id}`);
    return data;
  },
  runResearch: async (id) => {
    const { data } = await api.post(`/reels/${id}/research`);
    return data;
  },
  generateHooks: async (id) => {
    const { data } = await api.post(`/reels/${id}/hooks`);
    return data;
  },
  selectHook: async (id, hook) => {
    const { data } = await api.post(`/reels/${id}/select-hook`, { selected_hook: hook });
    return data;
  },
  generateCaption: async (id) => {
    const { data } = await api.post(`/reels/${id}/caption`);
    return data;
  },
  runAntiAiFilter: async (id) => {
    const { data } = await api.post(`/reels/${id}/anti-ai-filter`);
    return data;
  },
  approve: async (id) => {
    const { data } = await api.post(`/reels/${id}/approve`);
    trackEvent('post_approved', {
      ...currentProductEventProps(),
      content_ref: safeEntityRef(id),
      content_kind: 'reel_or_post',
      delivery_state: deliveryState(data?.delivery),
    });
    return data;
  },
  reject: async (id, reason = '') => {
    const { data } = await api.post(`/reels/${id}/reject`, { reason });
    trackEvent('post_needs_work', {
      ...currentProductEventProps(),
      content_ref: safeEntityRef(id),
      content_kind: 'reel_or_post',
      has_feedback: Boolean(String(reason || '').trim()),
      feedback_length_bucket: lengthBucket(reason),
    });
    return data;
  },
  restyle: async (id, style) => {
    const { data } = await api.post(`/reels/${id}/restyle`, { style });
    trackEvent('post_style_changed', {
      ...currentProductEventProps(),
      content_ref: safeEntityRef(id),
      content_kind: 'reel_or_post',
      style_ref: safeEntityRef(style),
    });
    return data;
  },
  updateCaption: async (id, caption) => {
    const { data } = await api.put(`/reels/${id}/caption`, { caption });
    trackEvent('post_edit_saved', {
      ...currentProductEventProps(),
      content_ref: safeEntityRef(id),
      content_kind: 'reel_or_post',
      edit_length_bucket: lengthBucket(caption),
    });
    return data;
  },
  styles: async () => {
    const { data } = await api.get('/styles');
    return data;
  },
  render: async (id, payload = {}) => {
    const { data } = await api.post(`/reels/${id}/render`, payload);
    return data;
  },
};

// Schedule / approval cockpit
export const scheduleApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/schedule/${workspaceId}`);
    return data;
  },
  // Move a post that lives in the Zernio queue to a new time (calendar drag).
  reschedulePost: async (workspaceId, postId, scheduledFor) => {
    const { data } = await api.put(`/schedule/${workspaceId}/posts/${postId}`, {
      scheduled_for: scheduledFor,
    });
    return data;
  },
};

// Text posts (composer)
export const postsApi = {
  compose: async (workspaceId, content, platforms, opts = {}) => {
    const { data } = await api.post('/posts/compose', {
      workspace_id: workspaceId,
      content,
      platforms,
      scheduled_for: opts.scheduledFor || null,
      publish_now: !!opts.publishNow,
      reel_id: opts.reelId || null,
    });
    return data;
  },
  list: async (workspaceId) => {
    const { data } = await api.get(`/posts/${workspaceId}`);
    return data;
  },
  // Reschedule a composed text post (also moves its Zernio queue entry when
  // one exists; the backend refuses the local move if Zernio says no).
  update: async (postId, scheduledFor) => {
    const { data } = await api.put(`/posts/${postId}`, { scheduled_for: scheduledFor });
    return data;
  },
};

// Coach API
export const coachApi = {
  getConversation: async (workspaceId) => {
    const { data } = await api.get(`/coach/${workspaceId}/conversation`);
    return data;
  },
  sendMessage: async (workspaceId, message, attachments = []) => {
    const { data } = await api.post(`/coach/${workspaceId}/message`, { message, attachments });
    return data;
  },
  getLaunchPadStatus: async (workspaceId) => {
    const { data } = await api.get(`/coach/${workspaceId}/launch-pad/status`);
    return data;
  },
  // Strategy/Offer Sheet (offer builder Phase 1 REST)
  getOfferSheet: async (workspaceId) => {
    const { data } = await api.get(`/coach/${workspaceId}/offer-sheet`);
    return data;
  },
  runOfferSheet: async (workspaceId) => {
    const { data } = await api.post(`/coach/${workspaceId}/offer-sheet/run`);
    return data;
  },
  lockOfferSheet: async (workspaceId) => {
    const { data } = await api.post(`/coach/${workspaceId}/offer-sheet/lock`);
    return data;
  },
};

// Fast Start onboarding, Core 12 questions, additive to the deep interview above.
export const fastStartApi = {
  getState: async (workspaceId) => {
    const { data } = await api.get(`/coach/${workspaceId}/fast-start/state`);
    return data;
  },
  submitLinks: async (workspaceId, payload) => {
    const { data } = await api.post(`/coach/${workspaceId}/fast-start/links`, payload);
    return data;
  },
  submitAnswer: async (workspaceId, questionId, text) => {
    const { data } = await api.post(`/coach/${workspaceId}/fast-start/answer`, {
      question_id: questionId,
      text,
    });
    return data;
  },
  submitVoiceAnswer: async (workspaceId, questionId, fileId) => {
    const { data } = await api.post(`/coach/${workspaceId}/fast-start/answer-voice`, {
      question_id: questionId,
      file_id: fileId,
    });
    return data;
  },
  generate: async (workspaceId) => {
    const { data } = await api.post(`/coach/${workspaceId}/fast-start/generate`);
    return data;
  },
};

// Content Ideas
export const ideasApi = {
  list: async (workspaceId) => {
    const { data } = await api.get(`/ideas?workspace_id=${workspaceId}`);
    return data;
  },
  create: async (workspaceId, category, text, tags = [], factBank = false) => {
    const { data } = await api.post(`/ideas?workspace_id=${workspaceId}`, {
      category, text, tags, fact_bank: factBank,
    });
    return data;
  },
  delete: async (id) => {
    await api.delete(`/ideas/${id}`);
  },
  promoteToReel: async (id) => {
    const { data } = await api.post(`/ideas/${id}/promote-to-reel`);
    return data;
  },
  generateImage: async (id) => {
    const { data } = await api.post(`/ideas/${id}/generate-image`);
    return data;
  },
};

// MyDay
export const mydayApi = {
  getCurrent: async (workspaceId) => {
    const { data } = await api.get(`/myday/${workspaceId}/current`);
    return data;
  },
  newWeek: async (workspaceId, weekLabel, hook = '') => {
    const { data } = await api.post(`/myday/${workspaceId}/week`, {
      week_label: weekLabel,
      this_weeks_hook: hook,
    });
    return data;
  },
  updateWeek: async (weekId, updates) => {
    const { data } = await api.put(`/myday/week/${weekId}`, updates);
    return data;
  },
  history: async (workspaceId) => {
    const { data } = await api.get(`/myday/${workspaceId}/history`);
    return data;
  },
  shipWeek: async (workspaceId) => {
    const { data } = await api.post(`/myday/${workspaceId}/ship-week`);
    return data;
  },
  aiIdeas: async (workspaceId, dayKey, count = 3) => {
    const { data } = await api.post(`/myday/${workspaceId}/ai-ideas`, {
      day_key: dayKey,
      count,
    });
    return data;
  },
  // Expand one short day-card idea into a full long-form post. The post is
  // written straight to the Cockpit approval queue (kind "post", review).
  expandIdea: async (workspaceId, dayKey, ideaIndex) => {
    const { data } = await api.post(`/myday/${workspaceId}/expand-idea`, {
      day_key: dayKey,
      idea_index: ideaIndex,
    });
    return data;
  },
  // Cross-week idea move (calendar drag). Pass either toWeekId or targetDate.
  moveIdea: async (workspaceId, { fromWeekId, dayKey, ideaIndex, toWeekId = null, targetDate = null }) => {
    const { data } = await api.post(`/myday/${workspaceId}/move-idea`, {
      from_week_id: fromWeekId,
      day_key: dayKey,
      idea_index: ideaIndex,
      to_week_id: toWeekId,
      target_date: targetDate,
    });
    return data;
  },
};

// Analytics (Zernio-backed; shows an empty state when not connected)
export const analyticsApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/analytics/${workspaceId}`);
    return data;
  },
};

// Evidence-linked advice. Reads/saves a review; never changes or publishes a post.
export const nicheReviewApi = {
  get: async (workspaceId) => (await api.get(`/niche-review/${workspaceId}`)).data,
  create: async (workspaceId, body) => (await api.post(`/niche-review/${workspaceId}`, body, { timeout: 130000 })).data,
};

// Modules
export const modulesApi = {
  list: async (workspaceId) => {
    const { data } = await api.get(`/modules/${workspaceId}`);
    return data;
  },
  toggle: async (workspaceId, moduleId, enabled) => {
    const { data } = await api.post(`/modules/${workspaceId}/toggle`, {
      module_id: moduleId,
      enabled,
    });
    return data;
  },
};

// AI Brain
export const aiBrainApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/ai-brain/${workspaceId}`);
    return data;
  },
  update: async (workspaceId, config) => {
    const { data } = await api.put(`/ai-brain/${workspaceId}`, config);
    return data;
  },
  testProvider: async (workspaceId, providerId) => {
    const { data } = await api.post(`/ai-brain/${workspaceId}/test-provider`, { provider_id: providerId });
    return data;
  },
};

// Autopilot
export const autopilotApi = {
  runReel: async (reelId) => {
    const { data } = await api.post(`/reels/${reelId}/autopilot`, { skip_clip_selection: true });
    return data;
  },
};

// Skool Plan
export const skoolPlanApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/skool-plan/${workspaceId}`);
    return data;
  },
  save: async (workspaceId, plan) => {
    const { data } = await api.put(`/skool-plan/${workspaceId}`, plan);
    return data;
  },
};

// Blog Article Writer
export const blogApi = {
  generate: async (workspaceId, topic, targetKeyword = null) => {
    const { data } = await api.post('/articles/generate', { workspace_id: workspaceId, topic, target_keyword: targetKeyword });
    return data;
  },
  list: async (workspaceId) => {
    const { data } = await api.get(`/articles/${workspaceId}`);
    return data;
  },
  save: async (articleId, updates) => {
    const { data } = await api.put(`/articles/${articleId}`, updates);
    return data;
  },
  publish: async (articleId, status = 'draft', target = 'wordpress') => {
    const { data } = await api.post(`/articles/${articleId}/publish`, { status, target });
    return data;
  },
  // [Suggest keywords] button (wave-2c): 10 keyword phrases, never a metric.
  keywordIdeas: async (workspaceId) => {
    const { data } = await api.post(`/articles/${workspaceId}/keyword-ideas`, {});
    return data;
  },
  publishTargets: async (workspaceId) => {
    const { data } = await api.get(`/articles/${workspaceId}/publish-targets`);
    return data;
  },
  getAutopilotSettings: async (workspaceId) => {
    const { data } = await api.get(`/articles/${workspaceId}/autopilot-settings`);
    return data;
  },
  setAutopilotSettings: async (workspaceId, settings) => {
    const { data } = await api.put(`/articles/${workspaceId}/autopilot-settings`, settings);
    return data;
  },
  runDailyNow: async (workspaceId) => {
    const { data } = await api.post(`/articles/${workspaceId}/autopilot-run-daily`, {});
    return data;
  },
  getSearchConsoleStatus: async (workspaceId) => {
    const { data } = await api.get(`/articles/${workspaceId}/search-console/status`);
    return data;
  },
  startSearchConsole: async (workspaceId) => {
    const { data } = await api.get(`/articles/${workspaceId}/search-console/start`);
    return data;
  },
  selectSearchConsoleProperty: async (workspaceId, siteUrl) => {
    const { data } = await api.put(`/articles/${workspaceId}/search-console/property`, { site_url: siteUrl });
    return data;
  },
  syncSearchConsole: async (workspaceId) => {
    const { data } = await api.post(`/articles/${workspaceId}/search-console/sync`, {});
    return data;
  },
};

// Client article review links are article-scoped and do not require a Glowup
// login. The token lives in the URL fragment, which browsers never send to
// nginx or analytics, and is forwarded only in this dedicated request header.
const reviewHeaders = (token) => ({ 'X-Client-Review-Token': token });

export const clientReviewApi = {
  get: async (articleId, token) => {
    const { data } = await api.get(`/client-review/articles/${articleId}`, { headers: reviewHeaders(token) });
    return data;
  },
  save: async (articleId, token, updates) => {
    const { data } = await api.put(`/client-review/articles/${articleId}`, updates, { headers: reviewHeaders(token) });
    return data;
  },
  decide: async (articleId, token, decision) => {
    const { data } = await api.post(`/client-review/articles/${articleId}/decision`, decision, { headers: reviewHeaders(token) });
    return data;
  },
};

// Hermes Guided AutoFill Tour (wave-2c section A)
export const autofillApi = {
  run: async (workspaceId, stage = null) => {
    const { data } = await api.post(`/autofill/${workspaceId}/run`, { stage });
    return data;
  },
  status: async (workspaceId) => {
    const { data } = await api.get(`/autofill/${workspaceId}/status`);
    return data;
  },
};

// Shared entry point for all 3 tour triggers (Start Here header button, Fast
// Start checkpoint, Coach GUIDE ME panel). The backend gates readiness (not
// answered Fast Start yet -> {status:'not_ready', redirect:'/coach'}); this
// helper just starts the run and tells the Coach panel to pick up narrating
// via a shared window event. Callers handle the not_ready case themselves
// (toast + navigate) since this file stays framework-agnostic.
export const startAutofillTour = async (workspaceId) => {
  const result = await autofillApi.run(workspaceId);
  if (result.status === 'started' || result.status === 'already_running') {
    window.dispatchEvent(new CustomEvent('glowup:start-autofill-tour', { detail: { workspaceId } }));
  }
  return result;
};

// Composer "Draft it for me" (wave-2c section B)
export const composerDraftApi = {
  draft: async (workspaceId, topic = '') => {
    const { data } = await api.post(`/composer/${workspaceId}/draft`, { topic });
    return data;
  },
};

// Approval-queue suggestion cards (wave-2c section E)
export const suggestionsApi = {
  approve: async (suggestionId) => {
    const { data } = await api.post(`/suggestions/${suggestionId}/approve`);
    return data;
  },
  dismiss: async (suggestionId) => {
    const { data } = await api.post(`/suggestions/${suggestionId}/dismiss`);
    return data;
  },
};

// LinkedIn Scan & Audit (wave-2c section F)
export const linkedinAuditApi = {
  run: async (workspaceId, paste = null) => {
    const { data } = await api.post(`/audit/${workspaceId}/linkedin`, paste || {});
    return data;
  },
  latest: async (workspaceId) => {
    const { data } = await api.get(`/audit/${workspaceId}/linkedin/latest`);
    return data;
  },
  screenshot: async (workspaceId, files) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const { data } = await api.post(`/audit/${workspaceId}/linkedin/screenshot`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

// Automation Map (wave-2c section G)
export const automationMapApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/automation-map/${workspaceId}`);
    return data;
  },
};

// UGC Studio (clone-avatar talking-head reels; kind == "ugc" on ReelProject)
export const ugcApi = {
  list: async (workspaceId) => {
    const { data } = await api.get(`/ugc?workspace_id=${workspaceId}`);
    return data;
  },
  // The list returns summaries; the full doc (script, broll_plan, render
  // instructions) is served by the shared reels endpoint.
  get: async (id) => {
    const { data } = await api.get(`/reels/${id}`);
    return data;
  },
  create: async (payload) => {
    const { data } = await api.post('/ugc', payload);
    return data;
  },
  generateScript: async (id) => {
    const { data } = await api.post(`/ugc/${id}/script`);
    return data;
  },
  generateBroll: async (id) => {
    const { data } = await api.post(`/ugc/${id}/broll`);
    return data;
  },
  render: async (id, engine = null) => {
    const { data } = await api.post(`/ugc/${id}/render`, { engine });
    return data;
  },
  autopilot: async (id, engine = null) => {
    const { data } = await api.post(`/ugc/${id}/autopilot`, { engine });
    return data;
  },
  approve: async (id) => {
    const { data } = await api.post(`/ugc/${id}/approve`);
    trackEvent('post_approved', {
      ...currentProductEventProps(),
      content_ref: safeEntityRef(id),
      content_kind: 'ugc',
      delivery_state: deliveryState(data?.delivery),
    });
    return data;
  },
  analyzeSample: async (workspaceId, { transcript, sourceUrl, productContext, avatarName } = {}) => {
    const { data } = await api.post('/ugc/analyze-sample', {
      workspace_id: workspaceId,
      transcript: transcript || null,
      source_url: sourceUrl || null,
      product_context: productContext || '',
      avatar_name: avatarName || null,
    });
    return data;
  },
  createMatrix: async (payload) => {
    const { data } = await api.post('/ugc/matrix', payload);
    return data;
  },
  getMatrix: async (id) => {
    const { data } = await api.get(`/ugc/matrix/${id}`);
    return data;
  },
};

// Connections
export const connectionsApi = {
  list: async (workspaceId) => {
    const { data } = await api.get(`/connections/${workspaceId}`);
    return data;
  },
  upsert: async (workspaceId, payload) => {
    const { data } = await api.post(`/connections/${workspaceId}`, payload);
    return data;
  },
  test: async (workspaceId, type) => {
    const { data } = await api.post(`/connections/${workspaceId}/test/${type}`);
    return data;
  },
  socialAccounts: async (workspaceId) => {
    const { data } = await api.get(`/social-accounts/${workspaceId}`);
    return data;
  },
};

// Notifications bell
export const notificationsApi = {
  get: async (workspaceId) => {
    const { data } = await api.get(`/notifications/${workspaceId}`);
    return data;
  },
  markRead: async (workspaceId) => {
    const { data } = await api.post(`/notifications/${workspaceId}/read`);
    return data;
  },
};

// Admin panel (Dylan-only; every call 403s for a non-admin token)
export const adminApi = {
  me: async () => {
    const { data } = await api.get('/admin/me');
    return data;
  },
  listWorkspaces: async () => {
    const { data } = await api.get('/admin/workspaces');
    return data;
  },
  listUsers: async () => {
    const { data } = await api.get('/admin/users');
    return data;
  },
  createWorkspace: async (payload) => {
    const { data } = await api.post('/admin/workspaces', payload);
    return data;
  },
  addMember: async (workspaceId, payload) => {
    const { data } = await api.post(`/admin/workspaces/${workspaceId}/members`, payload);
    return data;
  },
  getConnections: async (workspaceId) => {
    const { data } = await api.get(`/admin/workspaces/${workspaceId}/connections`);
    return data;
  },
  setConnection: async (workspaceId, payload) => {
    const { data } = await api.post(`/admin/workspaces/${workspaceId}/connections`, payload);
    return data;
  },
  listResellers: async () => {
    const { data } = await api.get('/admin/resellers');
    return data;
  },
  addResellerAdmin: async (sellerId, email) => {
    const { data } = await api.post(`/admin/resellers/${sellerId}/admins`, { email });
    return data;
  },
  reconcileResellerCost: async (invoiceId, currency, amountMinor) => {
    const { data } = await api.post(`/admin/reseller-settlements/${invoiceId}/direct-cost`, {
      currency,
      amount_minor: amountMinor,
    });
    return data;
  },
};

// Affiliates: your referral code, stats, and ledger. The commission terms
// (30% recurring, first 12 months) come from the backend response, never
// hardcoded here.
export const affiliateApi = {
  me: async () => {
    const { data } = await api.get('/affiliate/me');
    return data;
  },
};

export const sellerApi = {
  me: async () => {
    const { data } = await api.get('/seller/me');
    return data;
  },
  revenue: async () => {
    const { data } = await api.get('/seller/revenue');
    return data;
  },
  customers: async () => {
    const { data } = await api.get('/seller/customers');
    return data;
  },
};

// Billing (Stripe checkout). Backend returns 501 until Stripe keys are set on
// the deployment; callers treat that as "billing not live yet", never an error.
export const billingApi = {
  portal: async (workspaceId) => {
    const { data } = await api.post(`/billing/portal/${workspaceId}`);
    return data;
  },
  checkout: async (workspaceId, plan) => {
    const { data } = await api.post('/billing/checkout', { workspace_id: workspaceId, plan });
    return data; // { checkout_url, session_id, plan }
  },
  status: async (workspaceId) => {
    const { data } = await api.get(`/billing/status/${workspaceId}`);
    return data;
  },
  usage: async (workspaceId) => {
    const { data } = await api.get(`/billing/usage/${workspaceId}`);
    return data;
  },
  recommendation: async (workspaceId) => {
    const { data } = await api.get(`/billing/recommendation/${workspaceId}`);
    return data;
  },
};
